<template>
  <div id="mixed-tournament-preview" v-if="getMixedPreview">
    <v-progress-linear indeterminate color="var(--color-primary)" v-if="getMixedPreviewLoading" />


    <MixedEventsUnifiedPreview
            :live-events="getMixedPreview.liveEvents"
            :prematch-events="getMixedPreview.events"
            :header-sign-live="getMixedPreview.headerSignLive"
            :header-sign="getMixedPreview.headerSign"
            :tournament="getMixedPreview.tournament"
            :category="getMixedPreview.category"
            :sport="getMixedPreview.sport"
            :loading="getMixedPreviewLoading"
    />

    <div v-if="getMixedPreviewHasMore" class="load-more-container">
      <button
              :disabled="getMixedPreviewNextPageLoading"
              @click="fetchMixedPreviewNextPage"
          class="primary-button"
      >
        {{ $t('loadMore') || 'load more' }}
      </button>
    </div>

  </div>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex';
  import { isMobileOnly, isTablet } from 'mobile-device-detect';
  import MixedEventsUnifiedPreview from "@/components/center_widgets/Previews/MixedEventsUnifiedPreview.vue";
  import { MARKET_NOT_NAME_IN_OUTCOME } from '../../../constants/marketConstants';

  export default {
    name: 'MixedTournamentPreview',
    components: { MixedEventsUnifiedPreview },
    filters: {
      formatDecimal(value) {
        if (value === undefined || value === null) return '';
        const strVal = String(value).trim();
        if (strVal.includes('/') || strVal.startsWith('+') || strVal.startsWith('-')) return strVal;
        const num = parseFloat(strVal);
        if (isNaN(num)) return strVal;
        return num.toFixed(2);
      }
    },
    data: () => ({
      isMobileOnly,
      isTablet,
      isSmallScreen: false,
      maxMarkets: 3,
      infoColWidth: '350px',
      oddsVisualMode: 'blink',
      categoryPath: process.env.VUE_APP_URL_CLOUDFRONT + '/svg/categories/round_flags/',
      customLines: {},
      // Per-tournament market index selection (live / prematch independent)
      liveMarketIdx: {},     // { [tournamentId]: number }
      prematchMarketIdx: {}, // { [tournamentId]: number }
      activeTooltipId: null,
      activeMobileTooltipId: null,
      tooltipContent: '',
      tooltipStyle: {},
      tooltipPlacement: 'above',
    }),
    computed: {
      ...mapGetters({
        getMixedPreview:                'topsports/getMixedPreview',
        getMixedPreviewLoading:         'topsports/getMixedPreviewLoading',
        getMixedPreviewHasMore:         'topsports/getMixedPreviewHasMore',
        getMixedPreviewNextPageLoading: 'topsports/getMixedPreviewNextPageLoading',
        getOddsSources:                 'cart/getOddsSources',
        getLanguage:                    'settings/getLanguage',
        getCompetitorsInOutcome:        'settings/getCompetitorsInOutcome',
      }),
    },
    methods: {
      ...mapActions({
        clearMixedPreview:          'topsports/clearMixedPreview',
        fetchMixedPreviewNextPage:  'topsports/fetchMixedPreviewNextPage',
        addOddToCartV3:             'cart/addOddToCartV3',
        fetchEventDetailV3:         'detailPrematch/fetchEventDetailV3',
        fetchEventLiveDetailV5:     'detailLive/fetchEventLiveDetailV5',
        setLoadingPreview:          'odds/setLoadingPreview',
      }),



      isOddPlayable(odd) { return odd && odd.active && parseFloat(odd.outcome_odd_value) > 1; },


      getOutcomeName(competitors, odd, unique_id) {
        if (MARKET_NOT_NAME_IN_OUTCOME.includes(unique_id)) {
          return odd.outcome_alias || odd.outcome_name;
        }
        const outcome = odd.outcome_name?.toLowerCase();
        if (odd.outcome_name === 'X') return odd.outcome_alias || odd.outcome_name;
        if (this.getCompetitorsInOutcome) {
          if (outcome === 'local') return competitors?.[0]?.name || odd.outcome_alias || odd.outcome_name;
          if (outcome === 'visitante') return competitors?.[1]?.name || odd.outcome_alias || odd.outcome_name;
          if ((odd.outcome_name === '1' || odd.outcome_name === '2') && unique_id != '128' && competitors) {
            for (const c of competitors) {
              if (c.position === odd.outcome_name) return c.name;
            }
          }
        }
        return odd.outcome_alias || odd.outcome_name;
      },


      getSpecifierType(specifier) {
        if (specifier == null || String(specifier) === '') return 'neutral';
        const str = String(specifier).trim();
        if (str.startsWith('-')) return 'negative';
        if (str.startsWith('+')) return 'positive';
        return 'neutral';
      },
      stripScoreState(str) {
        if (!str) return str;
        return String(str).replace(/\s*\(\d+-\d+\)\s*$/, '').trim();
      },


      goToLiveDetail(event) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        this.fetchEventLiveDetailV5({
          source: event.id, group: 0,
          sport: event.sportId,
          tournament: event.tournamentId || '',
          status: event.status,
          sportName:    event.sportName,
          categoryName: event.categoryName,
          tournamentName: event.tournamentName,
          competitorHome:   event.home,
          competitorAway:   event.away,
          competitorHomeId: event.home_id,
          competitorAwayId: event.away_id,
        });
      },
      goToDetail(event, obj) {
        if (window.innerWidth < 1200 || window.forceMobileView === 'true') {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
        this.setLoadingPreview({ status: true });
        this.fetchEventDetailV3({
          source: event.id, group: null,
          sport: obj.sport.id, category: obj.category.id, tournament: obj.tournament.id,
          status: event.sport_event_status?._attributes?.status || event.status,
          startEventTime: event.scheduled,
          competitorHome: event.home, competitorAway: event.away,
          competitorHomeId: event.home_id, competitorAwayId: event.away_id,
          sportName: event.sportName,
          categoryName: event.categoryName,
          tournamentName: event.tournamentName
        }).then(() => this.setLoadingPreview({ status: false }));
      },


      positionTooltip(rect) {
        this.$nextTick(() => {
          const tooltipEl = this.$el && this.$el.querySelector('.custom-tooltip');
          if (!tooltipEl) return;
          const tipW = tooltipEl.offsetWidth;
          const tipH = tooltipEl.offsetHeight;
          const vw = window.innerWidth;
          const gap = 8;
          let top, transformY;
          if (rect.top - tipH - gap >= 0) {
            top = rect.top - gap;
            transformY = '-100%';
            this.tooltipPlacement = 'above';
          } else {
            top = rect.bottom + gap;
            transformY = '0%';
            this.tooltipPlacement = 'below';
          }
          const rawLeft = rect.left + rect.width / 2;
          let left, transformX;
          if (rawLeft - tipW / 2 < gap) { left = gap; transformX = '0%'; }
          else if (rawLeft + tipW / 2 > vw - gap) { left = vw - gap - tipW; transformX = '0%'; }
          else { left = rawLeft; transformX = '-50%'; }
          this.tooltipStyle = { top: top + 'px', left: left + 'px', transform: `translateX(${transformX}) translateY(${transformY})`, opacity: '1' };
        });
      },
      showTooltip(nativeEvent, eventId, content) {
        const rect = nativeEvent.currentTarget.getBoundingClientRect();
        this.tooltipContent = content;
        this.tooltipStyle = { top: '-9999px', left: '-9999px' };
        this.activeTooltipId = eventId;
        this.positionTooltip(rect);
      },
      hideTooltip() {
        this.activeTooltipId = null;
        this.tooltipStyle = { ...this.tooltipStyle, opacity: '0' };
      },

      onResize() {
        const w = window.innerWidth;
        this.isSmallScreen = w <= 600;
        if (w > 1350)      { this.maxMarkets = 3; this.infoColWidth = '350px'; }
        else if (w > 1000) { this.maxMarkets = 2; this.infoColWidth = '300px'; }
        else if (w > 600)  { this.maxMarkets = 1; this.infoColWidth = '250px'; }
        else               { this.maxMarkets = 1; this.infoColWidth = '110px'; }
      },
    },
    mounted() {
      this.onResize();
      window.addEventListener('resize', this.onResize);
    },
    beforeDestroy() {
      window.removeEventListener('resize', this.onResize);
    },
  };
</script>

<style scoped>
  .load-more-container {
    padding: 8px 0;
  display: flex;
  width: 100%;
  justify-content: center;
  }
</style>
